import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { getStreamApiUrl } from "@/config/runtime";
import { getHeaders } from "@/lib/api";

// ── Types ────────────────────────────────────────────────────────────────────

export type OperationStreamStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "completed"
  | "failed"
  | "error";

export interface OperationEvent {
  /** Sequence number from the backend ring buffer */
  seq: number;
  /** ISO timestamp */
  ts: string;
  /** Event name (e.g. "task_start", "step_content", "operation_completed") */
  event: string;
  /** Event payload — varies by event type */
  data: Record<string, unknown>;
}

/** Terminal event names — once received, the stream is done. */
const TERMINAL_EVENTS = new Set(["operation_completed", "operation_failed"]);

/** Max reconnection attempts before giving up. */
const MAX_RECONNECT_ATTEMPTS = 5;

/** Base delay for exponential backoff (ms). */
const RECONNECT_BASE_DELAY = 1000;

// ── SSE line parser ──────────────────────────────────────────────────────────

export interface SSEParseState {
  currentId: string | null;
  currentEvent: string | null;
  dataLines: string[];
}

export function createSSEParseState(): SSEParseState {
  return { currentId: null, currentEvent: null, dataLines: [] };
}

export function parseSSELine(line: string, state: SSEParseState): void {
  if (line.startsWith("id: ")) {
    state.currentId = line.slice(4).trim();
  } else if (line.startsWith("event: ")) {
    state.currentEvent = line.slice(7).trim();
  } else if (line.startsWith("data: ")) {
    state.dataLines.push(line.slice(6));
  }
  // SSE comment lines (starting with ":") are ignored by the caller
}

export function flushSSEEvent(
  state: SSEParseState,
  onEvent: (event: OperationEvent) => void,
  lastSeqRef: React.MutableRefObject<number | null>,
  terminalRef: React.MutableRefObject<boolean>,
  setStatus: (s: OperationStreamStatus) => void,
  onComplete?: () => void,
): void {
  if (!state.currentEvent || state.dataLines.length === 0) {
    resetSSEState(state);
    return;
  }

  try {
    const data = JSON.parse(state.dataLines.join("\n"));
    const seq = state.currentId ? Number.parseInt(state.currentId, 10) : (data.seq ?? 0);

    if (!Number.isNaN(seq) && seq > 0) {
      lastSeqRef.current = seq;
    }

    const operationEvent: OperationEvent = {
      seq,
      ts: data.ts || new Date().toISOString(),
      event: state.currentEvent,
      data: data.data === undefined ? data : data.data,
    };

    onEvent(operationEvent);

    if (TERMINAL_EVENTS.has(state.currentEvent)) {
      terminalRef.current = true;
      setStatus(state.currentEvent === "operation_completed" ? "completed" : "failed");
      onComplete?.();
    }
  } catch {
    // Skip malformed JSON
  }

  resetSSEState(state);
}

export function resetSSEState(state: SSEParseState): void {
  state.currentId = null;
  state.currentEvent = null;
  state.dataLines = [];
}

// ── Fetch & read helpers ─────────────────────────────────────────────────────

export async function fetchSSEStream(
  // NOSONAR -- fetch setup with necessary error handling branches
  operationId: string,
  lastSeq: number | null,
  controller: AbortController,
): Promise<Response> {
  const headers = await getHeaders();
  headers.set("Accept", "text/event-stream");
  headers.delete("Content-Type");

  let url = `${getStreamApiUrl()}/operations/${operationId}/stream`;
  if (lastSeq !== null) {
    url += `?last_event_id=${lastSeq}`;
  }

  const response = await fetch(url, {
    method: "GET",
    headers,
    signal: controller.signal,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
  }

  if (!response.body) {
    throw new Error("Response body is null");
  }

  return response;
}

export async function readSSEStream(
  // NOSONAR -- SSE parsing loop with inherent branching
  response: Response,
  onEventRef: React.MutableRefObject<(event: OperationEvent) => void>,
  lastSeqRef: React.MutableRefObject<number | null>,
  terminalRef: React.MutableRefObject<boolean>,
  setStatus: (s: OperationStreamStatus) => void,
  onCompleteRef: React.MutableRefObject<(() => void) | undefined>,
): Promise<void> {
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const state = createSSEParseState();

  const flush = () =>
    flushSSEEvent(state, onEventRef.current, lastSeqRef, terminalRef, setStatus, onCompleteRef.current);

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (line.startsWith(":") || false) {
        // SSE comment (heartbeat) — ignore
      } else if (line.trim() === "") {
        flush();
      } else {
        parseSSELine(line, state);
      }
    }
  }

  const remaining = decoder.decode();
  if (remaining) {
    buffer += remaining;
  }
  if (buffer.trim() === "") {
    flush();
  }
}

// ── Hook ─────────────────────────────────────────────────────────────────────

interface UseOperationStreamOptions {
  /** Called for every event received (including catchup events on reconnect). */
  onEvent: (event: OperationEvent) => void;
  /** Called when the stream reaches a terminal state. */
  onComplete?: () => void;
  /** Called on unrecoverable error. */
  onError?: (error: string) => void;
  /** Whether to automatically start the stream. Defaults to true. */
  enabled?: boolean;
}

interface UseOperationStreamReturn {
  /** Current connection status. */
  status: OperationStreamStatus;
  /** Manually abort the stream. */
  abort: () => void;
}

/**
 * Subscribe to an operation's SSE event stream from the OperationManager.
 *
 * Connects to `GET /api/v1/operations/{operationId}/stream` and handles:
 * - Initial connection with `last_event_id` for reconnection catchup
 * - Automatic reconnection with exponential backoff
 * - Parsing of SSE `id:`, `event:`, and `data:` fields
 * - Terminal event detection (`operation_completed`, `operation_failed`)
 *
 * The operation ID can be `null` initially (e.g. before the POST returns).
 * The stream only starts once `operationId` is non-null and `enabled` is true.
 */
export function useOperationStream(
  operationId: string | null,
  options: UseOperationStreamOptions,
): UseOperationStreamReturn {
  const { onEvent, onComplete, onError, enabled = true } = options;

  const [status, setStatus] = useState<OperationStreamStatus>("idle");

  // Refs to avoid stale closures in the async reader loop
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Track the last sequence number received for reconnection
  const lastSeqRef = useRef<number | null>(null);
  // Track reconnect attempts
  const reconnectCountRef = useRef(0);
  // AbortController for the active fetch
  const controllerRef = useRef<AbortController | null>(null);
  // Whether the hook has been intentionally aborted
  const abortedRef = useRef(false);
  // Whether a terminal event has been received
  const terminalRef = useRef(false);

  const abort = useCallback(() => {
    abortedRef.current = true;
    controllerRef.current?.abort();
    controllerRef.current = null;
  }, []);

  useEffect(() => {
    if (!operationId || !enabled) {
      setStatus("idle");
      return;
    }

    // Reset state for a new operation
    abortedRef.current = false;
    terminalRef.current = false;
    lastSeqRef.current = null;
    reconnectCountRef.current = 0;

    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = async () => {
      if (abortedRef.current || terminalRef.current) return;

      const isReconnect = reconnectCountRef.current > 0;
      setStatus(isReconnect ? "reconnecting" : "connecting");

      const controller = new AbortController();
      controllerRef.current = controller;

      try {
        const response = await fetchSSEStream(operationId, lastSeqRef.current, controller);

        setStatus("connected");
        reconnectCountRef.current = 0;

        await readSSEStream(response, onEventRef, lastSeqRef, terminalRef, setStatus, onCompleteRef);

        if (!terminalRef.current && !abortedRef.current) {
          scheduleReconnect();
        }
      } catch (error) {
        if (abortedRef.current || (error as Error).name === "AbortError") {
          return;
        }

        console.error("Operation stream error:", error);

        if (!terminalRef.current) {
          scheduleReconnect();
        }
      }
    };

    const scheduleReconnect = () => {
      if (abortedRef.current || terminalRef.current) return;

      reconnectCountRef.current += 1;

      if (reconnectCountRef.current > MAX_RECONNECT_ATTEMPTS) {
        setStatus("error");
        onErrorRef.current?.("Connection lost — too many reconnection attempts");
        return;
      }

      const delay = RECONNECT_BASE_DELAY * 2 ** (reconnectCountRef.current - 1);
      setStatus("reconnecting");
      reconnectTimer = setTimeout(connect, delay);
    };

    connect();

    return () => {
      abortedRef.current = true;
      controllerRef.current?.abort();
      controllerRef.current = null;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
    };
  }, [operationId, enabled]);

  return { status, abort };
}
